import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * YouTube AI Araştırma — serbest metinle talep yaz, Claude YouTube Data API'yi
 * kendi seçtiği sorgularla gezip rapor üretsin.
 *
 * Backend NDJSON akışı döner (satır başına bir olay), çünkü tek koşu dakikalar
 * sürebiliyor. Burada o akışı okuyup canlı adım günlüğü gösteriyoruz; koşu
 * bitince rapor + toplanan kanal/video listeleri geliyor.
 */

const REGIONS = [
  { code: 'TR', name: 'Türkiye' },
  { code: 'US', name: 'Amerika' },
  { code: 'GB', name: 'İngiltere' },
  { code: 'DE', name: 'Almanya' },
  { code: 'FR', name: 'Fransa' },
  { code: 'NL', name: 'Hollanda' },
  { code: 'ES', name: 'İspanya' },
  { code: 'IT', name: 'İtalya' },
  { code: 'SA', name: 'Suudi Arabistan' },
  { code: 'AE', name: 'BAE' },
  { code: 'IN', name: 'Hindistan' },
];

const EXAMPLES = [
  'Türkiye\'de bebek bakım ürünleri konuşan YouTube kanallarını çıkar; kim büyük, ne tür içerik çalışıyor, biz nereye girebiliriz?',
  'Son 3 ayda Türkiye\'de "araç kiralama" ve tatil ulaşımı etrafında ne izleniyor? Öne çıkan videoları ve kanalları analiz et.',
  '@arasteknoloji kanalını incele: ne sıklıkla yayınlıyor, hangi videoları tutmuş, izleyici yorumlarında ne konuşuluyor?',
  'Ev tekstili / yatak-yorgan kategorisinde Türkiye ve Almanya\'yı karşılaştır: içerik formatları ve büyük kanallar nasıl ayrışıyor?',
];

const TOOL_LABELS = {
  youtube_search: 'YouTube araması',
  youtube_trending: 'Popüler videolar',
  youtube_video_details: 'Video detayları',
  youtube_channel_details: 'Kanal detayları',
  youtube_channel_videos: 'Kanalın videoları',
  youtube_categories: 'Kategoriler',
  youtube_video_comments: 'Video yorumları',
};

const getApiBase = () => {
  if (typeof window === 'undefined') return 'https://api.glowytics.com';
  const h = window.location?.hostname || '';
  return (h === 'localhost' || h === '127.0.0.1') ? '' : 'https://api.glowytics.com';
};

const compact = (v) => {
  const n = Number(v || 0);
  if (!n) return '0';
  try { return new Intl.NumberFormat('tr', { notation: 'compact', maximumFractionDigits: 1 }).format(n); }
  catch (_) { return String(n); }
};

const duration = (sec) => {
  if (sec == null) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

function exportToCSV(rows, filename) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(','),
    ...rows.map((row) => headers.map((h) => {
      const str = String(row[h] ?? '').replace(/"/g, '""');
      return /[,"\n]/.test(str) ? `"${str}"` : str;
    }).join(',')),
  ];
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Araç girdisini tek satırlık okunur bir özete indirger.
function describeInput(name, input) {
  if (!input || typeof input !== 'object') return '';
  if (name === 'youtube_search') {
    const bits = [`"${input.q}"`];
    if (input.type === 'channel') bits.push('kanal');
    if (input.region) bits.push(input.region);
    if (input.order && input.order !== 'relevance') bits.push(input.order);
    if (input.publishedAfter) bits.push(`${String(input.publishedAfter).slice(0, 10)} sonrası`);
    return bits.join(' · ');
  }
  if (name === 'youtube_channel_videos') return input.channel || '';
  if (name === 'youtube_channel_details') {
    const list = input.channels || [];
    return list.slice(0, 3).join(', ') + (list.length > 3 ? ` +${list.length - 3}` : '');
  }
  if (name === 'youtube_video_details') return `${(input.videoIds || []).length} video`;
  if (name === 'youtube_video_comments') return input.videoId || '';
  if (name === 'youtube_trending') return [input.region, input.categoryId ? `kategori ${input.categoryId}` : ''].filter(Boolean).join(' · ');
  if (name === 'youtube_categories') return input.region || '';
  return '';
}

const MD_COMPONENTS = {
  h1: ({ children }) => <h2 className="text-lg font-bold mt-6 mb-2 text-gray-900">{children}</h2>,
  h2: ({ children }) => <h2 className="text-lg font-bold mt-6 mb-2 text-gray-900">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold mt-4 mb-1.5 text-gray-900">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold mt-3 mb-1 text-gray-800">{children}</h4>,
  p: ({ children }) => <p className="my-2 leading-relaxed text-gray-700">{children}</p>,
  ul: ({ children }) => <ul className="my-2 ml-5 list-disc space-y-1 text-gray-700">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 ml-5 list-decimal space-y-1 text-gray-700">{children}</ol>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer" className="text-red-600 hover:underline">{children}</a>,
  table: ({ children }) => <div className="overflow-x-auto my-3"><table className="min-w-full border-collapse text-sm">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => <th className="border border-gray-200 px-3 py-2 font-semibold text-left text-gray-600 whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="border border-gray-200 px-3 py-2 text-gray-700 align-top">{children}</td>,
  code: ({ children }) => <code className="bg-gray-100 px-1 py-0.5 rounded text-[0.85em] text-gray-800">{children}</code>,
};

export default function YouTubeAIResearch() {
  const [prompt, setPrompt] = useState('');
  const [region, setRegion] = useState('TR');
  const [running, setRunning] = useState(false);
  const [events, setEvents] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('report');
  const [elapsed, setElapsed] = useState(0);

  const abortRef = useRef(null);
  const logRef = useRef(null);

  useEffect(() => {
    if (!running) return undefined;
    const started = Date.now();
    const t = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(t);
  }, [running]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [events]);

  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setRunning(false);
  };

  const run = async () => {
    const text = prompt.trim();
    if (!text || running) return;

    setRunning(true);
    setEvents([]);
    setResult(null);
    setError(null);
    setElapsed(0);
    setTab('report');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const r = await fetch(`${getApiBase()}/api/youtube-research/run`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: text, region }),
        signal: controller.signal,
      });

      if (!r.ok || !r.body) {
        let msg = `HTTP ${r.status}`;
        try { const j = await r.json(); msg = j?.error || j?.details || msg; } catch (_) { /* gövde JSON değil */ }
        throw new Error(msg);
      }

      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // NDJSON: gelen son parça yarım satır olabilir, buffer'da bekletiyoruz.
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          let ev;
          try { ev = JSON.parse(line); } catch (_) { continue; }
          if (ev.type === 'ping') continue;
          if (ev.type === 'done') setResult(ev);
          else if (ev.type === 'error') {
            setError(ev.error);
            // Rapor yazılamasa bile toplanan kanal/video listesi gösterilsin.
            if ((ev.channels || []).length || (ev.videos || []).length) {
              setResult({ ...ev, report: '', partial: true });
              setTab('channels');
            }
          } else setEvents((prev) => [...prev, ev]);
        }
      }
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || 'Araştırma tamamlanamadı.');
    } finally {
      abortRef.current = null;
      setRunning(false);
    }
  };

  const channels = result?.channels || [];
  const videos = result?.videos || [];

  return (
    <div className="page-content p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <img src="/youtubelogov2.svg" alt="YouTube" className="h-6" />
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-900 text-white">AI Araştırma</span>
        </div>
        {result && (
          <div className="text-xs text-gray-500">
            {[
              result.steps != null ? `${result.steps} adım` : null,
              `${result.apiCalls} API çağrısı`,
              `${result.quotaUsed}${result.quotaBudget ? `/${result.quotaBudget}` : ''} kota`,
            ].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Ne öğrenmek istediğini normal cümlelerle yaz. Talebi anlayıp YouTube'da hangi sorguların atılacağına kendisi karar verir,
        veriyi toplar ve raporu yazar.
      </p>

      {/* Talep kutusu */}
      <div className="border border-gray-200 rounded-xl bg-white shadow-sm p-4 mb-5">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) run(); }}
          rows={4}
          disabled={running}
          placeholder="Örn: Türkiye'de evcil hayvan maması konuşan YouTube kanallarını çıkar; kim büyük, hangi format tutuyor, biz nereye girebiliriz?"
          className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-800 outline-none focus:border-gray-400 disabled:bg-gray-50"
        />
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            disabled={running}
            className="rounded-lg border border-gray-200 px-2.5 py-2 text-sm text-gray-700 disabled:bg-gray-50"
            title="Talepte başka ülke belirtmezsen bu bölge kullanılır"
          >
            {REGIONS.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
          </select>

          {running ? (
            <button onClick={stop} className="rounded-lg bg-gray-900 text-white px-4 py-2 text-sm font-medium hover:bg-gray-800">
              Durdur
            </button>
          ) : (
            <button
              onClick={run}
              disabled={!prompt.trim()}
              className="rounded-lg bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Araştır
            </button>
          )}
          <span className="text-xs text-gray-400">⌘/Ctrl + Enter</span>
          {running && <span className="text-xs text-gray-500 ml-auto">{elapsed} sn · genelde 1-3 dakika sürer</span>}
        </div>

        {!running && !result && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <div className="text-xs font-medium text-gray-400 mb-2">Örnek talepler</div>
            <div className="flex flex-col gap-1.5">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  onClick={() => setPrompt(ex)}
                  className="text-left text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded px-2 py-1.5 border border-transparent hover:border-gray-200"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Adım günlüğü */}
      {events.length > 0 && (
        <div className="mb-5 rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="px-4 py-2.5 border-b border-gray-100 text-xs font-semibold text-gray-500 flex items-center justify-between">
            <span>Araştırma adımları</span>
            {running && <span className="text-gray-400">çalışıyor…</span>}
          </div>
          <div ref={logRef} className="max-h-64 overflow-y-auto px-4 py-2 text-sm">
            {events.map((ev, i) => {
              if (ev.type === 'start') {
                return <div key={i} className="py-1 text-gray-400 text-xs">Başladı · bölge {ev.region} · kota bütçesi {ev.quotaBudget}</div>;
              }
              if (ev.type === 'thinking') {
                return <div key={i} className="py-1 text-gray-400 text-xs">{ev.final ? 'Rapor yazılıyor…' : `${ev.step}. tur — düşünüyor…`}</div>;
              }
              if (ev.type === 'tool_start') {
                return (
                  <div key={i} className="py-1 flex items-start gap-2">
                    <span className="text-gray-300 mt-0.5">→</span>
                    <span className="text-gray-800 font-medium whitespace-nowrap">{TOOL_LABELS[ev.name] || ev.name}</span>
                    <span className="text-gray-500 truncate">{describeInput(ev.name, ev.input)}</span>
                  </div>
                );
              }
              if (ev.type === 'tool_end') {
                return (
                  <div key={i} className={`py-0.5 pl-6 text-xs ${ev.error ? 'text-red-600' : 'text-gray-400'}`}>
                    {ev.error ? ev.error : `${ev.count != null ? `${ev.count} sonuç · ` : ''}kalan kota ${ev.quotaRemaining}`}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}

      {/* Sonuç */}
      {result && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-100 flex-wrap">
            {[
              ['report', 'Rapor'],
              ['channels', `Kanallar (${channels.length})`],
              ['videos', `Videolar (${videos.length})`],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-3 py-1.5 text-sm rounded-lg ${tab === key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {label}
              </button>
            ))}
            <div className="ml-auto flex gap-2">
              {tab === 'report' && !result.partial && (
                <button
                  onClick={() => navigator.clipboard?.writeText(result.report)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  Kopyala
                </button>
              )}
              {tab === 'channels' && channels.length > 0 && (
                <button
                  onClick={() => exportToCSV(channels.map((c) => ({
                    kanal: c.channelTitle, handle: c.handle, abone: c.subscribers,
                    video_sayisi: c.videoCount, toplam_izlenme: c.viewCount,
                    ulke: c.country, acilis: (c.publishedAt || '').slice(0, 10), url: c.url,
                  })), `youtube_kanallar_${new Date().toISOString().slice(0, 10)}.csv`)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  📥 CSV
                </button>
              )}
              {tab === 'videos' && videos.length > 0 && (
                <button
                  onClick={() => exportToCSV(videos.map((v) => ({
                    baslik: v.title, kanal: v.channelTitle, izlenme: v.views, begeni: v.likes,
                    yorum: v.comments, sure_sn: v.durationSec ?? '', tarih: (v.publishedAt || '').slice(0, 10), url: v.url,
                  })), `youtube_videolar_${new Date().toISOString().slice(0, 10)}.csv`)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  📥 CSV
                </button>
              )}
            </div>
          </div>

          {tab === 'report' && (
            <div className="px-6 py-4 text-sm">
              {result.partial ? (
                <div className="text-gray-500">
                  Rapor yazılamadı (koşu yarıda kesildi), ama toplanan kanal ve video verisi yandaki sekmelerde duruyor.
                </div>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{result.report}</ReactMarkdown>
              )}
            </div>
          )}

          {tab === 'channels' && (
            <div className="overflow-x-auto">
              {channels.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-gray-500">Bu koşuda kanal verisi toplanmadı.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Kanal</th>
                      <th className="text-right px-4 py-2 font-medium">Abone</th>
                      <th className="text-right px-4 py-2 font-medium">Video</th>
                      <th className="text-right px-4 py-2 font-medium">Toplam izlenme</th>
                      <th className="text-left px-4 py-2 font-medium">Ülke</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channels.map((c) => (
                      <tr key={c.channelId} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <a href={c.url} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 group">
                            {c.thumbUrl
                              ? <img src={c.thumbUrl} alt="" className="w-8 h-8 rounded-full object-cover bg-gray-100" />
                              : <span className="w-8 h-8 rounded-full bg-gray-100 inline-block" />}
                            <span>
                              <span className="block font-medium text-gray-900 group-hover:underline">{c.channelTitle || c.channelId}</span>
                              {c.handle && <span className="block text-xs text-gray-400">{c.handle}</span>}
                            </span>
                          </a>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{compact(c.subscribers)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{compact(c.videoCount)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{compact(c.viewCount)}</td>
                        <td className="px-4 py-2 text-gray-500">{c.country || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === 'videos' && (
            <div className="overflow-x-auto">
              {videos.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-gray-500">Bu koşuda video verisi toplanmadı.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Video</th>
                      <th className="text-right px-4 py-2 font-medium">İzlenme</th>
                      <th className="text-right px-4 py-2 font-medium">Beğeni</th>
                      <th className="text-right px-4 py-2 font-medium">Süre</th>
                      <th className="text-left px-4 py-2 font-medium">Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {videos.map((v) => (
                      <tr key={v.videoId} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <a href={v.url} target="_blank" rel="noreferrer" className="flex items-start gap-2.5 group max-w-xl">
                            <img src={v.thumbUrl} alt="" className="w-20 h-12 rounded object-cover bg-gray-100 flex-shrink-0" />
                            <span>
                              <span className="block font-medium text-gray-900 group-hover:underline">{v.title}</span>
                              <span className="block text-xs text-gray-400">{v.channelTitle}</span>
                            </span>
                          </a>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{v.views ? compact(v.views) : '—'}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{v.likes ? compact(v.likes) : '—'}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-500">{duration(v.durationSec)}</td>
                        <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{(v.publishedAt || '').slice(0, 10) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
